<template>
  <div class="registration-page">
    <!-- Header Section -->
    <header class="dashboard-header">
      <div class="header-content">
        <h1>User Registration</h1>
        <p class="subtitle">Register new staff for the system</p>
      </div>
    </header>

    <!-- Main Content -->
    <div class="registration-container">
      <div class="card">
        <div class="card-header">
          <h2>Registration Form</h2>
        </div>
        
        <div class="card-content">
          <form @submit.prevent="handleSubmit">
            <div class="form-group role-group">
              <label for="role">Role</label>
              <select
                id="role"
                v-model="formData.role"
                required
              >
                <option value="">Select role</option>
                <option value="Person-In-Charge">Person-In-Charge</option>
                <option value="Admin">Admin</option>
              </select>
            </div>

            <div class="form-group subsidiary-group">
              <label for="subsidiary">Subsidiary</label>
              <select
                id="subsidiary"
                v-model="formData.subsidiary"
                required
                :disabled="true"
              >
                <option :value="userSubsidiary">{{ userSubsidiaryName }}</option>
              </select>
            </div>

            <div class="form-group branch-group">
              <label for="branch">Branch</label>
              <select
                id="branch"
                v-model="formData.branch"
                required
                :disabled="!formData.subsidiary || formData.role !== 'Person-In-Charge'"
                :class="{ 'disabled-field': !formData.subsidiary || formData.role !== 'Person-In-Charge' }"
              >
                <option value="">Select branch</option>
                <option v-for="branch in branches" :key="branch.id" :value="branch.id">{{ branch.branch_name }}</option>
              </select>
              <p class="helper-text" v-if="formData.role && formData.role !== 'Person-In-Charge'">
                Branch selection is only available for Person-In-Charge (PIC) role
              </p>
              <p class="helper-text" v-if="formData.role === 'Person-In-Charge' && !formData.subsidiary">
                Please select a subsidiary first to enable branch selection
              </p>
            </div>

            <div class="form-grid">
              <div class="form-group">
                <label for="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  v-model="formData.fullName"
                  required
                  placeholder="Enter full name"
                />
              </div>

              <div class="form-group">
                <label for="phone">Phone Number <span class="optional">(optional)</span></label>
                <input
                  type="tel"
                  id="phone"
                  v-model="formData.phoneNumber"
                  placeholder="Enter phone number"
                />
              </div>

              <div class="form-group">
                <label for="staffNumber">Staff Number</label>
                <input
                  type="text"
                  id="staffNumber"
                  v-model="formData.staffNumber"
                  required
                  placeholder="Enter staff number"
                />
              </div>

              <div class="form-group">
                <label for="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  v-model="formData.email"
                  required
                  placeholder="Enter email address"
                />
              </div>

              <div class="form-group">
                <label for="password">Password</label>
                <div class="password-input">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    id="password"
                    v-model="formData.password"
                    required
                    placeholder="Enter password"
                  />
                  <button 
                    type="button" 
                    class="toggle-password"
                    @click="showPassword = !showPassword"
                  >
                    <i :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label for="confirmPassword">Confirm Password</label>
                <div class="password-input">
                  <input
                    :type="showConfirmPassword ? 'text' : 'password'"
                    id="confirmPassword"
                    v-model="formData.confirmPassword"
                    required
                    placeholder="Confirm password"
                  />
                  <button 
                    type="button" 
                    class="toggle-password"
                    @click="showConfirmPassword = !showConfirmPassword"
                  >
                    <i :class="showConfirmPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                  </button>
                </div>
              </div>
            </div>

            <div class="form-actions">
              <button
                type="submit"
                class="btn-primary"
                :disabled="isLoading"
              >
                {{ isLoading ? 'Registering...' : 'Register User' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <div class="toast-container" v-if="showToast">
      <div :class="['toast', toastType]">
        <span class="icon" v-if="toastType === 'success'">✓</span>
        {{ toastMessage }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const isLoading = ref(false)
const showToast = ref(false)
const toastMessage = ref('')
const toastType = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const userSubsidiary = ref('')
const userSubsidiaryName = ref('')
const branches = ref([])

const formData = ref({
  role: '',
  subsidiary: '',
  branch: '',
  fullName: '',
  phoneNumber: '',
  staffNumber: '',
  email: '',
  password: '',
  confirmPassword: ''
})

// API instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const showNotification = (message, type = 'success') => {
  toastMessage.value = message
  toastType.value = type
  showToast.value = true
  setTimeout(() => {
    showToast.value = false
  }, 3000)
}

const loadBranches = async () => {
  if (!formData.value.subsidiary) {
    branches.value = []
    formData.value.branch = ''
    return
  }

  try {
    const response = await api.get(`/api/branches?subsidiary_id=${userSubsidiary.value}`)
    branches.value = response.data
  } catch (error) {
    showNotification('Failed to load branches', 'error')
  }
}

const validateForm = async () => {
  
  
  // Check for empty required fields
  const requiredFields = ['role', 'subsidiary', 'fullName', 'staffNumber', 'email', 'password', 'confirmPassword']
  const emptyFields = requiredFields.filter(field => !formData.value[field])
  
  if (emptyFields.length > 0) {
    showNotification(`Please fill in all required fields: ${emptyFields.join(', ')}`, 'error')
    return false
  }

  if (formData.value.password !== formData.value.confirmPassword) {
    showNotification('Passwords do not match', 'error')
    return false
  }
  
  if (formData.value.password.length < 6) {
    showNotification('Password must be at least 6 characters long', 'error')
    return false
  }
  
  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(formData.value.email)) {
    showNotification('Please enter a valid email address', 'error')
    return false
  }
  
  if (formData.value.phoneNumber && !formData.value.phoneNumber.match(/^[0-9]{10,11}$/)) {
    showNotification('Please enter a valid phone number (10-11 digits)', 'error')
    return false
  }
  
  if (formData.value.role === 'Person-In-Charge' && !formData.value.branch) {
    showNotification('Please select a branch for Person-In-Charge role', 'error')
    return false
  }

  if (formData.value.phoneNumber?.trim()) {
    try {
      const response = await api.post('/api/users/check-phone', {
        phone: formData.value.phoneNumber
      })
      if (response.data.exists) {
        showNotification('This phone number is already registered', 'error')
        return false
      }
    } catch (error) {
      if (error.response?.data?.message) {
        showNotification(error.response.data.message, 'error')
      } else {
        showNotification('Error checking phone number', 'error')
      }
      return false
    }
  }

  return true
}

const handleSubmit = async () => {
  if (!(await validateForm())) return

  try {
    isLoading.value = true
    
    
    
    // Find the selected branch object
    const selectedBranch = branches.value.find(b => b.id === formData.value.branch)
    
    
    const payload = {
      role: formData.value.role,
      subsidiary: userSubsidiary.value,
      branch: formData.value.role === 'Person-In-Charge' ? String(formData.value.branch) : null,
      name: formData.value.fullName,
      phone: formData.value.phoneNumber?.trim() || null,
      staffNumber: formData.value.staffNumber,
      email: formData.value.email,
      password: formData.value.password
    }
    
    
    const response = await api.post('/api/users/register', payload)
    

    if (response.data.success) {
      showNotification('User registered successfully')
      formData.value = {
        role: '',
        subsidiary: userSubsidiary.value,
        branch: '',
        fullName: '',
        phoneNumber: '',
        staffNumber: '',
        email: '',
        password: '',
        confirmPassword: ''
      }
    } else {
      showNotification(response.data.message || 'Failed to register user', 'error')
    }
  } catch (error) {
    if (error.response) {
      if (error.response.status === 400) {
        const errorMessage = error.response.data.message || 'Invalid registration data'
        showNotification(errorMessage, 'error')
      } else if (error.response.status === 409) {
        showNotification('Email or phone number already exists', 'error')
      } else {
        showNotification(error.response.data.message || 'Failed to register user', 'error')
      }
    } else if (error.request) {
      showNotification('Could not connect to the server. Please try again later.', 'error')
    } else {
      showNotification('An error occurred. Please try again.', 'error')
    }
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  // Check if user is admin
  const userData = sessionStorage.getItem('user')
  if (userData) {
    const user = JSON.parse(userData)
    if (user.role !== 'Admin' && user.role !== 'admin') {
      router.push('/')
    } else {
      userSubsidiary.value = user.subsidiary_id
      userSubsidiaryName.value = user.subsidiary_name
      formData.value.subsidiary = user.subsidiary_id
      loadBranches()
    }
  } else {
    router.push('/')
  }
})
</script>

<style scoped>
.registration-page {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.header-content h1 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.025em;
}

.subtitle {
  font-size: 0.875rem;
  color: #64748b;
}

.registration-container {
  width: 100%;
}

.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.card-header {
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
}

.card-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.card-content {
  padding: 24px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.form-group {
  margin-bottom: 24px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
}

.optional {
  font-weight: 400;
  color: #94a3b8;
}

input,
select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #1e293b;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

input:hover {
  border-color: #94a3b8;
}

input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

select {
  padding-right: 40px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
}

select:hover {
  border-color: #94a3b8;
  background-color: #f8fafc;
}

select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background-color: white;
}

select option {
  padding: 12px;
  font-size: 0.875rem;
  color: #1e293b;
  background: white;
}

/* Style for the placeholder option */
select option[value=""] {
  color: #94a3b8;
}

.form-actions {
  margin-top: 32px;
}

.btn-primary {
  padding: 12px 24px;
  background: #1e40af;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 150px;
}

.btn-primary:hover {
  background: #1e3a8a;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.btn-primary:disabled {
  background: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.role-group,
.subsidiary-group,
.branch-group {
  margin-bottom: 24px;
}

.role-group select,
.subsidiary-group select,
.branch-group select {
  font-weight: 500;
}

.toast-container {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 1000;
  min-width: 300px;
}

.toast {
  padding: 16px;
  border-radius: 12px;
  font-weight: 500;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: slideIn 0.3s ease-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.toast.success {
  background-color: #dcfce7;
  color: #16a34a;
  border-color: rgba(22, 163, 74, 0.1);
}

.toast.error {
  background-color: #fee2e2;
  color: #dc2626;
  border-color: rgba(220, 38, 38, 0.1);
}

.toast .icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: currentColor;
  color: white;
  font-size: 14px;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .registration-page {
    padding: 8px;
  }
  .dashboard-header {
    margin-bottom: 16px;
  }
  .header-content h1 {
    font-size: 1.25rem;
  }
  .subtitle {
    font-size: 0.75rem;
  }
  .card {
    border-radius: 8px;
  }
  .card-header {
    padding: 12px;
    font-size: 1rem;
  }
  .card-header h2 {
    font-size: 1rem;
  }
  .card-content {
    padding: 12px;
  }
  .form-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .form-group {
    margin-bottom: 12px;
  }
  input,
  select {
    padding: 10px 12px;
    font-size: 0.8rem;
    border-radius: 6px;
  }
  .btn-primary {
    padding: 10px 16px;
    font-size: 0.8rem;
    min-width: 120px;
    border-radius: 6px;
  }
  .toast-container {
    left: 8px;
    right: 8px;
    min-width: unset;
  }
}

@media (max-width: 480px) {
  .registration-page {
    padding: 4px;
  }
  .card {
    border-radius: 6px;
  }
  .card-header {
    padding: 8px;
    font-size: 0.95rem;
  }
  .card-header h2 {
    font-size: 0.95rem;
  }
  .card-content {
    padding: 6px;
  }
  .form-group {
    margin-bottom: 8px;
  }
  input,
  select {
    padding: 8px 8px;
    font-size: 0.75rem;
    border-radius: 5px;
  }
  .btn-primary {
    padding: 8px 10px;
    font-size: 0.75rem;
    min-width: 100px;
    border-radius: 5px;
  }
  .toast-container {
    left: 4px;
    right: 4px;
    min-width: unset;
  }
}

.password-input {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input input {
  padding-right: 40px;
  width: 100%;
}

.toggle-password {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-password:hover {
  color: #1e293b;
}

.toggle-password i {
  font-size: 1rem;
}

.disabled-field {
  background-color: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
  border-color: #e2e8f0;
}

.helper-text {
  margin-top: 4px;
  font-size: 0.75rem;
  color: #64748b;
}

select.disabled-field:hover {
  border-color: #e2e8f0;
  background-color: #f1f5f9;
}

select.disabled-field:focus {
  box-shadow: none;
  border-color: #e2e8f0;
}
</style> 